import { ref } from '@vue/composition-api'
import draftApi from '@/services/api/drafts'
import useScrollToError from '@/composables/useScrollToError'
import EventBus from '@/store/EventBus'

export const defaultDraftForm = () => ({
  name: '',
  draft_id: null,
  noms_open: '',
  noms_closed: '',
  start_date: '',
  finish_date: '',
  draft_state_id: null,
  interval_num: 50,
  secretary_contact_name: '',
  secretary_phone_number: '',
  is_final: false,
  stock_levy: '20.00',
  draft_notes: '',
  event_members_allowed: false,
  is_show_biosecurity_form: false,
  required_vehicles: false,
  first_round_runs_per_person: '',
  total_runs_per_horse: '',
  provider_name: '',
  provider_contact_name: '',
  provider_phone: '',
  payments_due: '',
  online_payments_only: false,
  additional_attachment_1: null,
  additional_attachment_2: null,
  additional_attachment_1_url: null,
  additional_attachment_2_url: null,
  program: null,
  secretary_program: null,
  temporary_program: null,
  program_file: null,
  program_color: '#8c2535'
})

function toDatetimeLocal(str) {
  if (!str) return ''
  return str.replace(' ', 'T').substring(0, 16)
}

function toDateOnly(str) {
  if (!str) return ''
  return str.substring(0, 10)
}

function mapDraftToForm(draft) {
  const s = draft.settings != null ? draft.settings : {}
  return {
    name: draft.name || '',
    noms_open: toDatetimeLocal(draft.noms_open),
    noms_closed: toDatetimeLocal(draft.noms_closed),
    start_date: draft.start_date || '',
    finish_date: draft.end_date || '',
    draft_state_id: draft.state_id != null ? draft.state_id : null,
    is_final: !!draft.is_final,
    program: draft.program != null ? draft.program : null,
    secretary_program: draft.secretary_program != null ? draft.secretary_program : null,
    temporary_program: s.temporary_program != null ? s.temporary_program : null,
    interval_num: s.interval_num != null ? s.interval_num : 50,
    secretary_contact_name: s.secretary_contact_name || '',
    secretary_phone_number: s.secretary_phone_number || '',
    stock_levy: s.stock_levy || '20.00',
    draft_notes: s.draft_notes || '',
    event_members_allowed: !!s.event_members_allowed,
    is_show_biosecurity_form: !!s.is_show_biosecurity_form,
    required_vehicles: !!s.required_vehicles,
    first_round_runs_per_person: s.first_round_runs_per_person != null ? s.first_round_runs_per_person : '',
    total_runs_per_horse: s.total_runs_per_horse != null ? s.total_runs_per_horse : '',
    provider_name: s.provider_name || '',
    provider_contact_name: s.provider_contact_name || '',
    provider_phone: s.provider_phone || '',
    payments_due: toDateOnly(s.payments_due),
    online_payments_only: !!s.online_payments_only,
    additional_attachment_1: null,
    additional_attachment_2: null,
    additional_attachment_1_url: s.additional_attachment_1 != null ? s.additional_attachment_1 : null,
    additional_attachment_2_url: s.additional_attachment_2 != null ? s.additional_attachment_2 : null,
    program_color: s.program_color != null ? s.program_color : '#8c2535'
  }
}

export default function useDraftForm(form) {
  const errors = ref({})
  useScrollToError(errors)
  const submitting = ref(false)
  const submitted = ref(false)
  const loading = ref(false)
  const loadFailed = ref(false)
  const isStripeVerified = ref(false)

  async function loadDraft(id) {
    loading.value = true
    loadFailed.value = false
    try {
      const res = await draftApi.forEdit(id)
      Object.assign(form, mapDraftToForm(res.data.data))
      isStripeVerified.value = !!res.data.data.is_stripe_verified
    } catch (_err) {
      loadFailed.value = true
      EventBus.$emit('http-error-persistent', 'Draft not found or you do not have access to it.')
    } finally {
      loading.value = false
    }
  }

  async function reloadDraft(id) {
    try {
      const res = await draftApi.forEdit(id)
      Object.assign(form, mapDraftToForm(res.data.data))
      isStripeVerified.value = !!res.data.data.is_stripe_verified
    } catch (_err) {
      // silent — page stays visible, data stays as-is
    }
  }

  async function submit() {
    submitting.value = true
    errors.value = {}
    try {
      const res = await draftApi.setup({ ...form })
      submitted.value = true
      const id = res.data.data != null ? res.data.data.id : null
      return { message: res.data.message != null ? res.data.message : true, id }
    } catch (err) {
      const data = err.response != null ? err.response.data : null
      if (data != null && data.code === 'validation_error') {
        errors.value = data.errors
      }
      return false
    } finally {
      submitting.value = false
    }
  }

  function buildPayload(f) {
    return {
      name: f.name,
      noms_open: f.noms_open ? `${f.noms_open.replace('T', ' ')}:00` : null,
      noms_closed: f.noms_closed ? `${f.noms_closed.replace('T', ' ')}:00` : null,
      start_date: f.start_date || null,
      finish_date: f.finish_date || null,
      draft_state_id: f.draft_state_id,
      is_final: f.is_final,
      secretary_contact_name: f.secretary_contact_name,
      secretary_phone_number: f.secretary_phone_number,
      provider_name: f.provider_name,
      provider_contact_name: f.provider_contact_name,
      provider_phone: f.provider_phone,
      interval_num: f.interval_num,
      first_round_runs_per_person: f.first_round_runs_per_person !== '' ? f.first_round_runs_per_person : null,
      total_runs_per_horse: f.total_runs_per_horse !== '' ? f.total_runs_per_horse : null,
      stock_levy: f.stock_levy,
      payments_due: f.payments_due ? `${f.payments_due} 00:00:00` : null,
      online_payments_only: f.online_payments_only,
      event_members_allowed: f.event_members_allowed,
      is_show_biosecurity_form: f.is_show_biosecurity_form,
      required_vehicles: f.required_vehicles,
      draft_notes: f.draft_notes || null
    }
  }

  async function updateDraft(id) {
    submitting.value = true
    errors.value = {}
    try {
      const res = await draftApi.update(id, buildPayload(form))
      return res.data.message != null ? res.data.message : true
    } catch (err) {
      const data = err.response != null ? err.response.data : null
      if (data != null && data.code === 'validation_error') {
        errors.value = data.errors
      }
      return false
    } finally {
      submitting.value = false
    }
  }

  return { errors, submitting, submitted, submit, updateDraft, loading, loadFailed, isStripeVerified, loadDraft, reloadDraft }
}



// WEBPACK FOOTER //
// ./src/composables/drafts/useDraftForm.js